import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import NoteIcon from "@atlaskit/icon/core/note";
import PersonAvatarIcon from "@atlaskit/icon/core/person-avatar";
import SubtasksIcon from "@atlaskit/icon/core/subtasks";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import TagIcon from "@atlaskit/icon/core/tag";
import LinkExternalIcon from "@atlaskit/icon/core/link-external";
import type {NewCoreIconProps, NewIconProps} from "@atlaskit/icon/dist/types/types";
import {token} from "@atlaskit/tokens";
import {useQuery} from "@tanstack/react-query";
import {useNavigate} from "@tanstack/react-router";
import {
    buildEventContainerProfileFromForEventTemplate,
    buildEventContainerProfileNavigateSearch,
} from "~/utilities/eventContainerProfileNavigation";
import {buildJourneyProfilePath} from "~/utilities/entityProfileNavigation";
import {useMemo, useRef, useState, type JSX} from "react";
import {AnankeTempCollapsibleSectionWithIcon} from "~/components/anankeTemp/anankeTempCollapsibleSectionWithIcon";
import {AnankeTempFormCollapsibleSection} from "~/components/anankeTemp/anankeTempFormCollapsibleSection";
import {LinkedJourneyEventsOverviewSection} from "~/components/eventComponents/linkedJourneyEventsOverviewSection";
import {NoteFeedItem} from "~/components/notes/noteFeedItem";
import {collectEventContainerNoteListItems} from "~/domainModels/notes";
import {useWorkspace} from "~/contexts/workspaceContext";
import {
    EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__INPUT_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__INPUT_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LAST_TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__INPUT_SOURCE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__INPUT_TYPE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    EMOTORAD__USER__DEALERSHIP__COLUMN_ID,
    EMOTORAD__USER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__USER__EMAIL__COLUMN_ID,
    EmotoradEventTypes,
    EventCallType,
    emotoradEnquiryDigitalLeadStatuses,
    emotoradLeadEventStatuses,
    formatEmotoradScalarForUnknownColumnDisplay,
    formatEmotoradCallEventCurrentPlatformForDisplay,
    getEmotoradCallEventCurrentPlatformRaw,
    getEmotoradCurrentPlatformSelectOptions,
    getEmotoradInputSourceSelectOptions,
    getEmotoradInputTypeSelectOptions,
    isEmotoradWorkspace,
} from "~/domainModels/workspaceSpecific/emotorad";
import {
    AmaronEventTypes,
    amaronCallStatuses,
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    formatAmaronCallStatusIdForDisplay,
    isAmaronWorkspace,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    DULUX_EVENT_TEMPLATE_EVENT_OWNER_COLUMN_IDS,
    DuluxEventTypes,
    formatDuluxEventUserReferenceDisplay,
    getDuluxEventOwnerColumnIdForEventTemplate,
    getDuluxEventStatusColumnIdForEventTemplate,
    isDuluxEventTemplateId,
    isDuluxWorkspace,
} from "~/domainModels/workspaceSpecific/dulux";

import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {OttopilotEventViewPanelSectionKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTableViewColumn";
import {eventContainerEditPanelIconsMap} from "~/domainModels/eventContainerPanelIcons";

import {getSystemAdditionalColumnDetails} from "~/domainModels/systemAdditionalColumns";
import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {AnankeAvatar} from "@vesta/ananke/components/anankeAvatar";
import {AnankeHeading} from "@vesta/ananke/components/anankeHeading";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeLabel} from "@vesta/ananke/components/anankeLabel";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getOptionalOttopilotOrganizationalUnitValue} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotOrganizationalUnitColumnConfig";
import {uniqueUuid} from "@vesta/unique/unique";
import {
    getEventsWithEventContainers,
    type EventContainerWithEvents,
    type EventWithEventContainer,
} from "~/utilities/apis/api";
import {useBtlActivities} from "~/contexts/workspaceBtlActivitiesContext";
import type {BtlActivity} from "~/domainModels/btlActivities";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {formatDateAndTime, formatDateTime, formatOttopilotDisplayDateTime, getUserDisplayName, getUserRole} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {
    formatEmotoradTestRideDateOnlyForDisplay,
    formatEmotoradTestRideShortRangeLabelFromSlotHhMm,
    getEmotoradTestRideEffectiveSlotHhMmFromEventRow,
} from "~/utilities/workspaceSpecific/emotoradTestRideTimeSlots";

export type ResolvedColumn = {
    id: Uuid;
    label: string;
    columnId: Uuid | string;
    isAdditionalColumn: boolean;
    columnType: "OttopilotEvent" | "OttopilotEventContainer";
    eventTemplateId?: Uuid;
};

export type ResolvedColumnGroupSection = {
    kind: OttopilotEventViewPanelSectionKind.columnGroup;
    id: Uuid;
    displayName: string;
    icon?: (props: NewCoreIconProps) => JSX.Element;
    iconColor?: NewIconProps["color"];
    iconBackgroundColor?: string;
    initialIsOpen: boolean;
    className: string;
    columns: Array<ResolvedColumn>;
};

export type ResolvedEventOverviewSection = {
    kind: OttopilotEventViewPanelSectionKind.eventOverview;
    id: Uuid;
    displayName?: string;
    initialIsOpen: boolean;
    className: string;
};

export type ResolvedViewPanelSection = ResolvedColumnGroupSection | ResolvedEventOverviewSection;

function getSectionsFromConfig(getAdditionalColumns: CapabilityUdoColumnResolver, eventTemplate: OttopilotEventTemplate, isExpanded: boolean, useLineSeparatedSections: boolean): Array<ResolvedViewPanelSection> {
    const expandedClassName =
        useLineSeparatedSections === true ? "py-ds-space-100 px-ds-space-200" : "border-ds-border rounded-ds-border-radius-200 p-ds-space-200";
    const collapsedClassName = "border-ds-border py-ds-space-100 px-ds-space-200";
    const firstSectionClassName = `border-t border-ds-border ${isExpanded ? expandedClassName : collapsedClassName}`;
    const sectionClassName = firstSectionClassName;
    const secondSectionClassName = isExpanded
        ? useLineSeparatedSections === true
            ? `border-t border-ds-border ${expandedClassName}`
            : expandedClassName
        : `border-t ${collapsedClassName}`;

    const viewPanel = eventTemplate.configuration.eventPage.viewPanel;

    if (viewPanel === undefined || viewPanel.sections.length === 0) {
        return [
            {
                kind: OttopilotEventViewPanelSectionKind.columnGroup,
                id: "fallback-event-details" as Uuid,
                displayName: "Event details",
                icon: InformationCircleIcon,
                iconColor: "var(--ds-icon-accent-blue)" as NewIconProps["color"],
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                className: sectionClassName,
                columns: getAdditionalColumns(eventTemplate).map((col) => ({
                    id: col.id,
                    label: col.displayName,
                    columnId: col.id,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                })),
            },
            {
                kind: OttopilotEventViewPanelSectionKind.columnGroup,
                id: "fallback-timeline" as Uuid,
                displayName: "Timeline",
                icon: SubtasksIcon,
                iconColor: "var(--ds-icon-accent-purple)" as NewIconProps["color"],
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: true,
                className: secondSectionClassName,
                columns: [
                    {
                        id: "fallback-created-at" as Uuid,
                        label: "Created on",
                        columnId: "createdAt",
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                        isAdditionalColumn: false,
                    },
                    {
                        id: "fallback-updated-at" as Uuid,
                        label: "Last modified on",
                        columnId: "updatedAt",
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                        isAdditionalColumn: false,
                    },
                ],
            },
            {
                kind: OttopilotEventViewPanelSectionKind.columnGroup,
                id: "fallback-event-metadata" as Uuid,
                displayName: "Event metadata",
                icon: PersonAvatarIcon,
                iconColor: "var(--ds-icon-accent-red)" as NewIconProps["color"],
                iconBackgroundColor: "var(--ds-background-accent-red-subtlest)",
                initialIsOpen: false,
                className: secondSectionClassName,
                columns: [
                    {
                        id: "fallback-created-by" as Uuid,
                        label: "Created by",
                        columnId: "createdBy",
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                        isAdditionalColumn: false,
                    },
                    {
                        id: "fallback-updated-by" as Uuid,
                        label: "Last modified by",
                        columnId: "updatedBy",
                        columnType: "OttopilotEvent" as const,
                        eventTemplateId: eventTemplate.id,
                        isAdditionalColumn: false,
                    },
                ],
            },
        ];
    }

    return viewPanel.sections.map((section, index) => {
        const className = section.className ?? (index === 0 ? sectionClassName : secondSectionClassName);
        if (section.kind === OttopilotEventViewPanelSectionKind.eventOverview) {
            return {
                kind: OttopilotEventViewPanelSectionKind.eventOverview,
                id: section.id,
                displayName: section.displayName,
                initialIsOpen: section.initialIsOpen ?? true,
                className,
            };
        }
        const icon = section.iconId !== undefined ? eventContainerEditPanelIconsMap[section.iconId] : undefined;
        return {
            kind: OttopilotEventViewPanelSectionKind.columnGroup,
            id: section.id,
            displayName: section.displayName,
            icon,
            iconColor: section.iconColor as NewIconProps["color"] | undefined,
            iconBackgroundColor: section.iconBackgroundColor,
            initialIsOpen: section.initialIsOpen ?? true,
            className,
            columns: section.columns.map((col) => ({
                id: col.id,
                label: col.label,
                columnId: col.columnId,
                columnType: col.columnType,
                eventTemplateId: col.columnType === "OttopilotEvent" ? col.eventTemplateId : undefined,
                isAdditionalColumn: col.isAdditionalColumn,
            })),
        };
    });
}

export type ColumnValueDeps = {
    row: EventWithEventContainer;
    journeysModuleConfiguration: JourneysModuleConfiguration;
    eventTemplates: Array<OttopilotEventTemplate>;
    users: ReturnType<typeof useUsers>;
    userTemplates: ReturnType<typeof useUserTemplates>;
    platformUserDetailsByUserId: ReadonlyMap<Uuid, ReturnType<typeof useWorkspace>["platformUserDetails"][number]>;
    organizationalUnits: ReturnType<typeof useOrganizationalUnits>;
    organizationalUnitTemplates: ReturnType<typeof useOrganizationalUnitTemplates>;
    btlActivities: ReturnType<typeof useBtlActivities>;
    products: ReturnType<typeof useProductCatalogue>;
    userDefinedObjectOptionsByTemplateId: ReturnType<typeof useWorkspace>["userDefinedObjectOptionsByTemplateId"];
};

const USER_REFERENCE_COLUMN_LABELS = new Set([
    "assigned to",
    "assigned agent",
    "assignee",
    "lead owner",
    "lifecycle owner",
    "event owner",
    "created by",
    "modified by",
    "updated by",
    "last modified by",
]);

function emotoradNormalizedStringHasContent(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    return String(value).trim() !== "";
}

function resolveEmotoradCallsCustomerGeoMerged(row: EventWithEventContainer, field: "state" | "city"): unknown {
    const eventKey = field === "state" ? EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID : EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID;
    const journeyKey = field === "state" ? EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID : EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID;
    const fromEvent = row.additionalColumnValues[eventKey];
    if (emotoradNormalizedStringHasContent(fromEvent) === true) return fromEvent;
    return row.eventContainer.additionalColumnValues[journeyKey];
}

function resolveEmotoradKamRmFromAssignedDealer(
    row: EventWithEventContainer,
    field: "kam" | "rm",
    organizationalUnits: ColumnValueDeps["organizationalUnits"],
    organizationalUnitTemplates: ColumnValueDeps["organizationalUnitTemplates"],
    users: ColumnValueDeps["users"],
    userTemplates: ColumnValueDeps["userTemplates"],
    platformUserDetailsByUserId: ColumnValueDeps["platformUserDetailsByUserId"],
): string {
    const assignedDealerRaw = row.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
    const assignedDealerId = typeof assignedDealerRaw === "string" && assignedDealerRaw.trim() !== "" ? (assignedDealerRaw as Uuid) : null;
    if (assignedDealerId === null) return "";
    const fieldLabels = field === "kam" ? ["KAM", "KAM Name", "Kam Name"] : ["RM", "RM Name", "Rm Name", "ZSM", "ZSM Name", "Zsm Name"];
    const fieldKeywords = field === "kam" ? ["kam", "key account"] : ["rm", "zsm", "regional", "relationship"];

    const getFromOu = (ouId: Uuid): string | null => {
        const ou = organizationalUnits.find((unit) => unit.id === ouId);
        if (ou === undefined) return null;
        const template = organizationalUnitTemplates.find((item) => item.id === ou.templateId);
        if (template === undefined) return null;
        for (const label of fieldLabels) {
            const column = template.additionalColumns.find((item) => item.displayName.trim().toLowerCase() === label.toLowerCase());
            if (column === undefined) continue;
            const raw = ou.additionalColumnValues[column.id];
            if (raw !== null && raw !== undefined && String(raw).trim() !== "") return String(raw);
        }
        for (const column of template.additionalColumns) {
            const normalizedLabel = column.displayName.trim().toLowerCase();
            if (fieldKeywords.some((keyword) => normalizedLabel.includes(keyword)) !== true) continue;
            const raw = ou.additionalColumnValues[column.id];
            if (raw !== null && raw !== undefined && String(raw).trim() !== "") return String(raw);
        }
        return null;
    };

    const visited = new Set<Uuid>();
    let currentId: Uuid | null = assignedDealerId;
    while (currentId !== null && visited.has(currentId) === false) {
        visited.add(currentId);
        const value = getFromOu(currentId);
        if (value !== null) return value;
        const currentOu = organizationalUnits.find((unit) => unit.id === currentId);
        currentId = currentOu?.parentId ?? null;
    }

    for (const user of users) {
        const linkedDealerships = user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID];
        const linkedIds = Array.isArray(linkedDealerships)
            ? linkedDealerships
                  .map((item) => (typeof item === "string" ? item : ((item as Record<string, unknown>).id ?? (item as Record<string, unknown>).value)))
                  .filter((item): item is string => typeof item === "string")
            : [
                  typeof linkedDealerships === "string"
                      ? linkedDealerships
                      : ((linkedDealerships as Record<string, unknown> | null)?.id ?? (linkedDealerships as Record<string, unknown> | null)?.value),
              ].filter((item): item is string => typeof item === "string");
        if (linkedIds.includes(assignedDealerId) !== true) continue;
        const template = userTemplates.find((item) => item.id === user.templateId);
        const templateName = template?.displayName?.toLowerCase() ?? "";
        if (
            (field === "kam" && templateName.includes("kam") === false) ||
            (field === "rm" && templateName.includes("rm") === false && templateName.includes("zsm") === false)
        ) {
            continue;
        }
        const name = getUserDisplayName(user.userId, users, userTemplates, platformUserDetailsByUserId);
        if (name !== "?" && name !== "-") return name;
    }
    return "";
}

/** Align read-only view panel empty cells with table display (— / -), not unresolved "?". */
function normalizeViewPanelColumnDisplay(value: string | null | undefined): string {
    if (value === null || value === undefined) {
        return "-";
    }
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === "?") {
        return "-";
    }
    return value;
}

export function resolveColumnValue(getAdditionalColumns: CapabilityUdoColumnResolver, col: ResolvedColumn, deps: ColumnValueDeps): string {
    const {
        row,
        journeysModuleConfiguration,
        eventTemplates,
        users,
        userTemplates,
        platformUserDetailsByUserId,
        organizationalUnits,
        organizationalUnitTemplates,
        products,
        userDefinedObjectOptionsByTemplateId,
    } = deps;

    const resolveUserReference = (value: unknown): string | null => {
        if (typeof value !== "string" || value.trim() === "") {
            return null;
        }
        const displayName = getUserDisplayName(value.trim() as Uuid, users, userTemplates, platformUserDetailsByUserId);
        return displayName === "-" || displayName === "?" ? null : displayName;
    };

    // Get raw value
    let rawValue: unknown;
    if (col.columnType === "OttopilotEventContainer") {
        rawValue = col.isAdditionalColumn
            ? row.eventContainer.additionalColumnValues[col.columnId as Uuid]
            : (row.eventContainer as unknown as Record<string, unknown>)[col.columnId as string];
    } else {
        rawValue = col.isAdditionalColumn
            ? row.additionalColumnValues[col.columnId as Uuid]
            : (row as unknown as Record<string, unknown>)[col.columnId as string];
        if (
            col.isAdditionalColumn &&
            (rawValue === null || rawValue === undefined || rawValue === "") &&
            String(col.columnId) === String(EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID)
        ) {
            rawValue = row.additionalColumnValues[EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID];
        }
    }

    if (row.templateId === (EmotoradEventTypes.call as Uuid)) {
        if (col.columnType === "OttopilotEvent" && col.isAdditionalColumn === true) {
            const cid = String(col.columnId);
            if (cid === String(EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID)) {
                rawValue = resolveEmotoradCallsCustomerGeoMerged(row, "state");
            } else if (cid === String(EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID)) {
                rawValue = resolveEmotoradCallsCustomerGeoMerged(row, "city");
            } else if (cid === String(EMOTORAD__EVENT__CALLS__CURRENT_PLATFORM__COLUMN_ID)) {
                return formatEmotoradCallEventCurrentPlatformForDisplay(getEmotoradCallEventCurrentPlatformRaw(row.additionalColumnValues));
            }
        }
        if (col.columnType === "OttopilotEventContainer" && col.isAdditionalColumn === true) {
            const jid = String(col.columnId);
            if (jid === String(EMOTORAD__EVENT_CONTAINER__KAM_NAME__COLUMN_ID) && emotoradNormalizedStringHasContent(rawValue) === false) {
                const resolved = resolveEmotoradKamRmFromAssignedDealer(
                    row,
                    "kam",
                    organizationalUnits,
                    organizationalUnitTemplates,
                    users,
                    userTemplates,
                    platformUserDetailsByUserId,
                );
                if (resolved !== "") rawValue = resolved;
            } else if (jid === String(EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID) && emotoradNormalizedStringHasContent(rawValue) === false) {
                const resolved = resolveEmotoradKamRmFromAssignedDealer(
                    row,
                    "rm",
                    organizationalUnits,
                    organizationalUnitTemplates,
                    users,
                    userTemplates,
                    platformUserDetailsByUserId,
                );
                if (resolved !== "") rawValue = resolved;
            }
        }
    }

    /**
     * Disposition tracker view panel marks this field as an Event column but binds the eventContainer
     * "created by role" id, so the value is never read from `event.additionalColumnValues`.
     * Align with the table: show the event author's role template name.
     */
    if (col.columnType === "OttopilotEvent" && col.isAdditionalColumn === true && String(col.columnId) === String(EMOTORAD__EVENT_CONTAINER__CREATED_BY_ROLE__COLUMN_ID)) {
        const createdByUser = users.find((user) => user.userId === row.createdBy);
        const template = userTemplates.find((userTemplate) => userTemplate.id === createdByUser?.templateId);
        return template?.displayName ?? "-";
    }

    const resolveAssignedDealerFieldFromFallbacks = (field: "contact" | "email"): string | null => {
        const assignedDealerRaw = row.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
        const findOuByPossibleId = (raw: unknown) => {
            if (typeof raw !== "string" || raw.trim() === "") return undefined;
            return organizationalUnits.find((unit) => unit.id === (raw as Uuid));
        };
        const assignedDealerOu =
            findOuByPossibleId(assignedDealerRaw) ??
            findOuByPossibleId(getOptionalOttopilotOrganizationalUnitValue(
                row.eventContainer,
                journeysModuleConfiguration.configuration.organizationalUnitColumnConfig,
            )) ??
            findOuByPossibleId(row.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__DEALER_PINCODE__COLUMN_ID]) ??
            findOuByPossibleId(row.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID]) ??
            findOuByPossibleId(row.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID]);
        const displayNames =
            field === "contact"
                ? ["Contact Number", "Phone Number", "Mobile Number", "Dealer Contact", "Dealer Contact Number"]
                : ["Email", "Email Address", "Dealer Email"];
        const keywords = field === "contact" ? ["contact", "phone", "mobile", "whatsapp", "number"] : ["email", "mail"];

        const fromOu = (unit: (typeof organizationalUnits)[number]): string | null => {
            const template = organizationalUnitTemplates.find((item) => item.id === unit.templateId);
            if (template === undefined) return null;
            for (const name of displayNames) {
                const c = template.additionalColumns.find((column) => column.displayName.trim().toLowerCase() === name.toLowerCase());
                if (c === undefined) continue;
                const v = unit.additionalColumnValues[c.id];
                if (typeof v === "string" && v.trim() !== "") return v.trim();
            }
            for (const c of template.additionalColumns) {
                const label = c.displayName.trim().toLowerCase();
                if (keywords.some((k) => label.includes(k)) !== true) continue;
                const v = unit.additionalColumnValues[c.id];
                if (typeof v === "string" && v.trim() !== "") return v.trim();
            }
            return null;
        };

        if (assignedDealerOu !== undefined) {
            const visited = new Set<Uuid>();
            let current: (typeof organizationalUnits)[number] | undefined = assignedDealerOu;
            while (current !== undefined && visited.has(current.id) === false) {
                visited.add(current.id);
                const value = fromOu(current);
                if (value !== null) return value;
                current = current.parentId !== null ? organizationalUnits.find((unit) => unit.id === current?.parentId) : undefined;
            }
        }

        const dealerId = assignedDealerOu?.id ?? (typeof assignedDealerRaw === "string" ? (assignedDealerRaw as Uuid) : null);
        if (dealerId === null) return null;
        const fieldColumnId = field === "contact" ? EMOTORAD__USER__CONTACT_NUMBER__COLUMN_ID : EMOTORAD__USER__EMAIL__COLUMN_ID;
        for (const user of users) {
            const rawDealership = user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID];
            const dealershipIds = Array.isArray(rawDealership)
                ? rawDealership.map((item) => (typeof item === "string" ? item : ((item as any)?.id ?? (item as any)?.value))).filter(Boolean)
                : [typeof rawDealership === "string" ? rawDealership : ((rawDealership as any)?.id ?? (rawDealership as any)?.value)].filter(Boolean);
            if (dealershipIds.includes(dealerId) !== true) continue;
            const value = user.additionalColumnValues[fieldColumnId];
            if (typeof value === "string" && value.trim() !== "") return value.trim();
        }
        return null;
    };

    const isJourneyEmotoradAssignedDealerContactOrEmail =
        col.columnType === "OttopilotEventContainer" &&
        col.isAdditionalColumn === true &&
        (String(col.columnId) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID) ||
            String(col.columnId) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID));
    if (
        isJourneyEmotoradAssignedDealerContactOrEmail &&
        (rawValue === null || rawValue === undefined || (typeof rawValue === "string" && rawValue.trim() === ""))
    ) {
        return (
            resolveAssignedDealerFieldFromFallbacks(
                String(col.columnId) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID) ? "contact" : "email",
            ) ?? "-"
        );
    }

    if (
        col.isAdditionalColumn === true &&
        col.columnType === "OttopilotEvent" &&
        col.eventTemplateId === (EmotoradEventTypes.testride as Uuid) &&
        String(col.columnId) === String(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID)
    ) {
        const slot = getEmotoradTestRideEffectiveSlotHhMmFromEventRow(row);
        if (slot !== "") {
            return formatEmotoradTestRideShortRangeLabelFromSlotHhMm(slot);
        }
    }

    if (rawValue === null || rawValue === undefined) {
        if (String(col.columnId) === String(EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID)) {
            const userDisplayName = getUserDisplayName(row.createdBy, users, userTemplates, platformUserDetailsByUserId);
            return userDisplayName !== "?" ? userDisplayName : String(row.createdBy);
        }
        if (String(col.columnId) === String(EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID)) {
            const createdByUser = users.find((user) => user.userId === row.createdBy);
            const template = userTemplates.find((userTemplate) => userTemplate.id === createdByUser?.templateId);
            return template?.displayName ?? "-";
        }
        if (String(col.columnId) === String(EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID)) {
            const userDisplayName = getUserDisplayName(row.createdBy, users, userTemplates, platformUserDetailsByUserId);
            return userDisplayName !== "?" ? userDisplayName : String(row.createdBy);
        }
        if (String(col.columnId) === String(EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID)) {
            const createdByUser = users.find((user) => user.userId === row.createdBy);
            const template = userTemplates.find((userTemplate) => userTemplate.id === createdByUser?.templateId);
            return template?.displayName ?? "-";
        }
        return "-";
    }

    if (col.isAdditionalColumn === true && col.columnType === "OttopilotEvent" && String(col.columnId) === String(AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID)) {
        if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") {
            return "-";
        }
        return formatAmaronCallStatusIdForDisplay(String(rawValue));
    }

    if (col.columnType === "OttopilotEvent" && col.isAdditionalColumn === true && typeof rawValue === "string" && rawValue.trim() !== "") {
        const cid = String(col.columnId);
        if (cid === String(EMOTORAD__EVENT__CALLS__INPUT_TYPE__COLUMN_ID) || cid === String(EMOTORAD__EVENT__WALKINS__INPUT_TYPE__COLUMN_ID)) {
            const inputOpt = getEmotoradInputTypeSelectOptions().find((o) => String(o.id) === rawValue);
            if (inputOpt !== undefined) return inputOpt.displayName;
            if (rawValue === EventCallType["inbound"]) return "Inbound";
            if (rawValue === EventCallType["outBound"]) return "Outbound";
        }
        if (cid === String(EMOTORAD__EVENT__CALLS__INPUT_SOURCE__COLUMN_ID) || cid === String(EMOTORAD__EVENT__WALKINS__INPUT_SOURCE__COLUMN_ID)) {
            const srcOpt = getEmotoradInputSourceSelectOptions().find((o) => String(o.id) === rawValue);
            if (srcOpt !== undefined) return srcOpt.displayName;
        }
    }

    if (
        row.templateId === (EmotoradEventTypes.call as Uuid) &&
        col.columnType === "OttopilotEventContainer" &&
        col.isAdditionalColumn === true &&
        String(col.columnId) === String(EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID) &&
        typeof rawValue === "string" &&
        rawValue.trim() !== ""
    ) {
        const platformOpt = getEmotoradCurrentPlatformSelectOptions().find((o) => String(o.id) === rawValue);
        if (platformOpt !== undefined) return platformOpt.displayName;
    }

    if (String(col.columnId) === String(EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID)) {
        return getUserDisplayName(rawValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
    }
    if (String(col.columnId) === String(EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID)) {
        const userDisplayName = getUserDisplayName(row.createdBy, users, userTemplates, platformUserDetailsByUserId);
        return userDisplayName !== "?" ? userDisplayName : String(row.createdBy);
    }
    if (String(col.columnId) === String(EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID)) {
        const createdByUser = users.find((user) => user.userId === row.createdBy);
        const template = userTemplates.find((userTemplate) => userTemplate.id === createdByUser?.templateId);
        return template?.displayName ?? "-";
    }
    if (String(col.columnId) === String(EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID)) {
        const userDisplayName = getUserDisplayName(row.createdBy, users, userTemplates, platformUserDetailsByUserId);
        return userDisplayName !== "?" ? userDisplayName : String(row.createdBy);
    }
    if (String(col.columnId) === String(EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID)) {
        const createdByUser = users.find((user) => user.userId === row.createdBy);
        const template = userTemplates.find((userTemplate) => userTemplate.id === createdByUser?.templateId);
        return template?.displayName ?? "-";
    }

    // Emotorad: Assigned Dealer on eventContainer may be stored as OU id while column type is not `ou`.
    if (
        col.columnType === "OttopilotEventContainer" &&
        col.isAdditionalColumn === true &&
        String(col.columnId) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID) &&
        typeof rawValue === "string"
    ) {
        const trimmed = rawValue.trim();
        if (trimmed !== "") {
            const ou = organizationalUnits.find((u) => u.id === trimmed);
            if (ou !== undefined) {
                const ouTemplate = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
                if (ouTemplate !== undefined) {
                    return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
                }
            }
        }
    }

    if (col.isAdditionalColumn === true && col.columnType === "OttopilotEvent" && col.eventTemplateId === (EmotoradEventTypes.testride as Uuid)) {
        const cid = String(col.columnId);
        if (cid === String(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID)) {
            if (rawValue === null || rawValue === undefined || rawValue === "") return "-";
            const trimmed = String(rawValue).trim();
            const formatted = formatEmotoradTestRideDateOnlyForDisplay(trimmed);
            return formatted !== "-" ? formatted : trimmed;
        }
        if (
            cid === String(EMOTORAD__EVENT__TESTRIDES__LAST_TEST_RIDE_DATE__COLUMN_ID) ||
            cid === String(EMOTORAD__EVENT__TESTRIDES__SCHEDULED_ON__COLUMN_ID) ||
            cid === String(EMOTORAD__EVENT__TESTRIDES__LOGGED_ON__COLUMN_ID) ||
            cid === String(EMOTORAD__EVENT__TESTRIDES__CANCELLED_ON__COLUMN_ID)
        ) {
            if (rawValue === null || rawValue === undefined || rawValue === "") return "-";
            const trimmed = String(rawValue).trim();
            const formatted = formatDateTime(trimmed);
            return formatted !== "-" ? formatted : trimmed;
        }
    }

    const normalizedLabel = col.label.trim().toLowerCase();
    if (USER_REFERENCE_COLUMN_LABELS.has(normalizedLabel) === true) {
        const displayName = resolveUserReference(rawValue);
        if (displayName !== null) {
            return displayName;
        }
    }
    if (col.columnType === "OttopilotEvent" && col.isAdditionalColumn === true) {
        const eventTemplateForColumn = eventTemplates.find((template) => template.id === col.eventTemplateId);
        const eventColumnDefinition = getAdditionalColumns(eventTemplateForColumn).find((column) => column.id === col.columnId);
        if (eventColumnDefinition?.type === UdoColumnType.user) {
            return resolveUserReference(rawValue) ?? "-";
        }
    }

    // Additional column
    if (col.isAdditionalColumn) {
        if (col.columnType === "OttopilotEventContainer") {
            const colDef = getAdditionalColumns(journeysModuleConfiguration).find((c) => c.id === col.columnId);
            if (colDef === undefined) {
                return normalizeViewPanelColumnDisplay(formatEmotoradScalarForUnknownColumnDisplay(rawValue));
            }
            if (colDef.type === UdoColumnType.user) {
                return resolveUserReference(rawValue) ?? "-";
            }
            return normalizeViewPanelColumnDisplay(
                getUserDefinedObjectAdditionalColumnDetails(colDef).stringValueGetter({
                    value: rawValue as never,
                    users,
                    userTemplates,
                    platformUserDetailsByUserId,
                    organizationalUnits,
                    organizationalUnitTemplates,
                    products: products ?? [],
                    userDefinedObjectOptionsByTemplateId,
                }),
            );
        } else {
            const eventTemplate = eventTemplates.find((et) => et.id === col.eventTemplateId);
            const colDef = getAdditionalColumns(eventTemplate).find((c) => c.id === col.columnId);
            if (colDef === undefined || eventTemplate === undefined) {
                return normalizeViewPanelColumnDisplay(formatEmotoradScalarForUnknownColumnDisplay(rawValue));
            }
            if (colDef.type === UdoColumnType.user) {
                return resolveUserReference(rawValue) ?? "-";
            }
            if (DULUX_EVENT_TEMPLATE_EVENT_OWNER_COLUMN_IDS.some((id) => String(id) === String(col.columnId))) {
                const display = getUserDisplayName(rawValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
                return normalizeViewPanelColumnDisplay(display !== "?" ? display : String(rawValue ?? "-"));
            }
            return normalizeViewPanelColumnDisplay(
                getUserDefinedObjectAdditionalColumnDetails(colDef).stringValueGetter({
                    value: rawValue as never,
                    users,
                    userTemplates,
                    platformUserDetailsByUserId,
                    organizationalUnits,
                    organizationalUnitTemplates,
                    products: products ?? [],
                    userDefinedObjectOptionsByTemplateId,
                }),
            );
        }
    }

    // System column
    const systemDetails = getSystemAdditionalColumnDetails(String(col.columnId));
    if (systemDetails !== undefined) {
        const systemKey = col.columnId as string;
        const isDuluxEvent = col.columnType === "OttopilotEvent" && isDuluxEventTemplateId(row.templateId) === true;
        if (systemKey === "updatedBy" || systemKey === "createdBy") {
            if (isDuluxEvent === true) {
                return normalizeViewPanelColumnDisplay(
                    formatDuluxEventUserReferenceDisplay(rawValue, {
                        users: users,
                        userTemplates: userTemplates,
                        platformUserDetailsByUserId: platformUserDetailsByUserId,
                    }),
                );
            }
            const userDisplayName = getUserDisplayName(rawValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
            return normalizeViewPanelColumnDisplay(userDisplayName);
        }
        if ((systemKey === "createdAt" || systemKey === "updatedAt") && isDuluxEvent === true) {
            return normalizeViewPanelColumnDisplay(formatOttopilotDisplayDateTime(String(rawValue)));
        }
        return normalizeViewPanelColumnDisplay(
            systemDetails.stringValueGetter({
                value: rawValue,
                users,
                userTemplates,
                platformUserDetailsByUserId,
            }),
        );
    }

    return "-";
}

function TruncatedFieldValue({value}: {value: string}) {
    const textRef = useRef<HTMLSpanElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleClick = () => {
        if (isExpanded) {
            setIsExpanded(false);
            return;
        }
        const el = textRef.current;
        if (el !== null && el.scrollWidth > el.clientWidth + 1) {
            setIsExpanded(true);
        }
    };

    return (
        <div className="min-w-0 w-full">
            <span
                ref={textRef}
                onClick={handleClick}
                className="text-ds-font-body !font-ds-font-weight-regular text-ds-text cursor-pointer block max-w-full"
                title={isExpanded ? undefined : value}
                style={
                    isExpanded
                        ? {whiteSpace: "normal", wordBreak: "break-word"}
                        : {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                          }
                }
            >
                {value}
            </span>
        </div>
    );
}

function EventViewSection({section, deps}: {section: ResolvedColumnGroupSection; deps: ColumnValueDeps}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const oddColumns = section.columns.filter((_, index) => (index + 1) % 2 === 1);
    const evenColumns = section.columns.filter((_, index) => (index + 1) % 2 === 0);
    const rowCount = Math.max(oddColumns.length, evenColumns.length);

    const renderColumnField = (col: ResolvedColumn) => {
        const displayValue = resolveColumnValue(getAdditionalColumns, col, deps);
        return (
            <div
                key={col.id}
                className="grid grid-rows-[max-content_max-content] gap-y-ds-space-050 min-w-0"
            >
                <AnankeLabel>{col.label}</AnankeLabel>
                <TruncatedFieldValue value={displayValue} />
            </div>
        );
    };

    const columnContent =
        section.columns.length <= 1 ? (
            <div className="grid grid-flow-row gap-y-ds-space-200 min-w-0">{section.columns.map(renderColumnField)}</div>
        ) : (
            <div className="grid grid-flow-row gap-y-ds-space-200 min-w-0">
                {Array.from({length: rowCount}).map((_, rowIndex) => (
                    <div
                        key={`row-${rowIndex}`}
                        className="grid grid-cols-2 gap-x-ds-space-300 items-start min-w-0"
                    >
                        <div className="min-w-0">{oddColumns[rowIndex] !== undefined ? renderColumnField(oddColumns[rowIndex]!) : null}</div>
                        <div className="min-w-0">{evenColumns[rowIndex] !== undefined ? renderColumnField(evenColumns[rowIndex]!) : null}</div>
                    </div>
                ))}
            </div>
        );

    if (section.icon !== undefined && section.iconColor !== undefined && section.iconBackgroundColor !== undefined) {
        return (
            <AnankeTempCollapsibleSectionWithIcon
                title={section.displayName}
                icon={section.icon}
                iconColor={section.iconColor}
                iconBackgroundColor={section.iconBackgroundColor}
                initialIsOpen={section.initialIsOpen}
                className={section.className}
            >
                {columnContent}
            </AnankeTempCollapsibleSectionWithIcon>
        );
    }

    return (
        <AnankeTempFormCollapsibleSection
            title={section.displayName}
            initialIsOpen={section.initialIsOpen}
            className={section.className}
        >
            {columnContent}
        </AnankeTempFormCollapsibleSection>
    );
}

export function EventViewPanel({
    row,
    isExpanded,
    eventTemplate,
    journeysModuleConfiguration,
    eventTemplates,
    notesEventContainerSourceLabel,
    linkedJourneyEventTemplateIds,
}: {
    row: EventWithEventContainer;
    isExpanded: boolean;
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
    eventTemplates: Array<OttopilotEventTemplate>;
    notesEventContainerSourceLabel?: string;
    linkedJourneyEventTemplateIds?: Array<Uuid>;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const resolvedNotesEventContainerSourceLabel =
        notesEventContainerSourceLabel ??
        journeysModuleConfiguration.configuration.moduleDisplayNameVariants.singularSentenceCase;
    const navigate = useNavigate();
    const {workspace, workspaceCapabilities, platformUserDetails, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const mountedAtRef = useRef(Date.now());
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const products = useProductCatalogue();
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );
    const noteItems = collectEventContainerNoteListItems({
        eventContainer: row.eventContainer,
        eventTemplates,
        resolveAuthor: (userId) => {
            const user = users.find((item) => item.id === userId);
            const templateId = user?.templateId ?? null;
            return {
                displayName: getUserDisplayName(userId, users, userTemplates, platformUserDetailsByUserId),
                roleLabel: templateId !== null ? getUserRole(templateId, userTemplates) : "",
            };
        },
        eventContainerSourceLabel: resolvedNotesEventContainerSourceLabel,
        sortDirection: "recent",
    });

    const isEmotoradInqueueStyledEventPanel =
        isEmotoradWorkspace(workspace) === true &&
        (eventTemplate.id === (EmotoradEventTypes.call as Uuid) ||
            eventTemplate.id === (EmotoradEventTypes.sales as Uuid) ||
            eventTemplate.id === (EmotoradEventTypes.walkin as Uuid) ||
            eventTemplate.id === (EmotoradEventTypes.lead as Uuid) ||
            eventTemplate.id === (EmotoradEventTypes.testride as Uuid));
    const isAmaronScaffoldStyledEventPanel =
        isAmaronWorkspace(workspaceCapabilities) === true &&
        (eventTemplate.id === (AmaronEventTypes.Calls as Uuid) ||
            eventTemplate.id === (AmaronEventTypes.Enquiry as Uuid) ||
            eventTemplate.id === (AmaronEventTypes.Service as Uuid));
    const isDuluxScaffoldStyledEventPanel =
        isDuluxWorkspace(workspaceCapabilities) === true &&
        (eventTemplate.id === (DuluxEventTypes.Calls as Uuid) ||
            eventTemplate.id === (DuluxEventTypes.Appointments as Uuid) ||
            eventTemplate.id === (DuluxEventTypes.Enquiry as Uuid) ||
            eventTemplate.id === (DuluxEventTypes.Onboarding as Uuid));
    const isScaffoldStyledEventPanel =
        isEmotoradInqueueStyledEventPanel === true ||
        isAmaronScaffoldStyledEventPanel === true ||
        isDuluxScaffoldStyledEventPanel === true;
    const sections = useMemo(() => {
        return getSectionsFromConfig(getAdditionalColumns, eventTemplate, isExpanded, isScaffoldStyledEventPanel);
    }, [getAdditionalColumns, eventTemplate, isExpanded, isScaffoldStyledEventPanel]);
    const hasEventsOverview = sections.some(
        (section) => section.kind === OttopilotEventViewPanelSectionKind.eventOverview,
    );
    const linkedJourneyEventsQuery = useQuery({
        queryKey: ["event-view-linked-journey-events", workspace.id, row.eventContainer.id],
        enabled: hasEventsOverview === true && linkedJourneyEventTemplateIds === undefined,
        queryFn: async (): Promise<Array<Uuid>> => {
            const result = await getEventsWithEventContainers({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                filterConfiguration: {
                    revisionId: uniqueUuid`1806476f-9798-4f87-887a-43626957ab49`,
                    lineItems: [
                        {
                            columnId: "eventContainerId",
                            operation: FilterOperation.equals,
                            value: row.eventContainer.id,
                        },
                    ],
                },
                sortConfiguration: {
                    revisionId: uniqueUuid`ee9af531-737c-4883-a627-9666f95b74a7`,
                    lineItems: [],
                },
                limit: 1000,
                offset: 0,
                workspaceId: workspace.id,
            });
            if (result.success !== true) {
                throw result.error;
            }
            return result.data.map((event) => event.templateId);
        },
    });

    const deps: ColumnValueDeps = {
        row,
        journeysModuleConfiguration,
        eventTemplates,
        users,
        userTemplates,
        platformUserDetailsByUserId,
        organizationalUnits,
        organizationalUnitTemplates,
        btlActivities,
        products,
        userDefinedObjectOptionsByTemplateId,
    };

    const nestedEvents = (row.eventContainer as Partial<EventContainerWithEvents>).events;
    const fallbackOverviewTemplateIds =
        Array.isArray(nestedEvents) === true
            ? nestedEvents.map((event) => event.templateId)
            : [row.templateId];
    const overviewTemplateIds =
        linkedJourneyEventTemplateIds ??
        linkedJourneyEventsQuery.data ??
        fallbackOverviewTemplateIds;

    const renderSection = (section: ResolvedViewPanelSection) => {
        if (section.kind === OttopilotEventViewPanelSectionKind.eventOverview) {
            return (
                <LinkedJourneyEventsOverviewSection
                    key={section.id}
                    title={section.displayName ?? "Events on this lead"}
                    eventTemplates={eventTemplates}
                    linkedJourneyEventTemplateIds={overviewTemplateIds}
                    initialIsOpen={section.initialIsOpen}
                    className={section.className}
                />
            );
        }
        return (
            <EventViewSection
                key={section.id}
                section={section}
                deps={deps}
            />
        );
    };

    const customerName = processOttopilotStringTemplate(
        journeysModuleConfiguration.configuration.displayNameTemplate,
        row.eventContainer.additionalColumnValues,
    );

    const isEmotoradLeadEvent = isEmotoradWorkspace(workspace) === true && eventTemplate.id === (EmotoradEventTypes.lead as Uuid);

    const isDuluxWorkspaceForPanel = isDuluxWorkspace(workspaceCapabilities) === true;

    const dispositionText = (() => {
        if (isDuluxWorkspaceForPanel === true) {
            const statusColumnId = getDuluxEventStatusColumnIdForEventTemplate(eventTemplate.id);
            if (statusColumnId !== undefined) {
                const resolved = resolveColumnValue(
                    getAdditionalColumns,
                    {
                        id: statusColumnId,
                        label: "Status",
                        columnId: statusColumnId,
                        columnType: "OttopilotEvent",
                        eventTemplateId: eventTemplate.id,
                        isAdditionalColumn: true,
                    },
                    deps,
                );
                if (resolved !== "-") {
                    return resolved;
                }
            }
        }
        if (isEmotoradLeadEvent === true) {
            const leadRaw = row.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID];
            if (leadRaw !== null && leadRaw !== undefined && String(leadRaw).trim() !== "") {
                const leadId = String(leadRaw);
                const fromEnum = Object.values(emotoradLeadEventStatuses).find((s) => s.id === leadId);
                if (fromEnum !== undefined) {
                    return fromEnum.displayName;
                }
            }
            const digitalRaw = row.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID];
            if (digitalRaw !== null && digitalRaw !== undefined && String(digitalRaw).trim() !== "") {
                const digitalId = String(digitalRaw);
                const fromEnum = Object.values(emotoradEnquiryDigitalLeadStatuses).find((s) => s.id === digitalId);
                if (fromEnum !== undefined) {
                    return fromEnum.displayName;
                }
            }
            const leadResolved = resolveColumnValue(
                getAdditionalColumns,
                {
                    id: EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
                    label: "Lead Status",
                    columnId: EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
                    columnType: "OttopilotEvent",
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                },
                deps,
            );
            if (leadResolved !== "-") {
                return leadResolved;
            }
            const digitalResolved = resolveColumnValue(
                getAdditionalColumns,
                {
                    id: EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
                    label: "Digital Lead Status",
                    columnId: EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
                    columnType: "OttopilotEvent",
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                },
                deps,
            );
            if (digitalResolved !== "-") {
                return digitalResolved;
            }
        }
        if (eventTemplate.configuration.statusColumnConfig !== undefined) {
            return resolveColumnValue(
                getAdditionalColumns,
                {
                    id: eventTemplate.configuration.statusColumnConfig.columnId,
                    label: "Status",
                    columnId: eventTemplate.configuration.statusColumnConfig.columnId,
                    columnType: "OttopilotEvent",
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                },
                deps,
            );
        }
        return "-";
    })();

    const {date, time} = formatDateAndTime(row.createdAt);
    const eventDateTime = date !== "-" && time !== "-" ? `${date}, ${time}` : "-";
    // For Amaron Calls: show Call Status in the tag-icon pill instead of UTM Platform
    const isAmaronCallsEvent = isAmaronWorkspace(workspaceCapabilities) === true && eventTemplate.id === (AmaronEventTypes.Calls as Uuid);
    const tagPillText = (() => {
        if (isAmaronCallsEvent) {
            const rawStatusId = row.additionalColumnValues[AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID] as string | null | undefined;
            if (rawStatusId == null) return "-";
            const match = Object.values(amaronCallStatuses).find((s) => s.id === rawStatusId);
            return match?.displayName ?? "-";
        }
        const eventUtmPlatformColumn = getAdditionalColumns(eventTemplate).find((column) => column.displayName.trim().toLowerCase() === "utm platform");
        const journeyUtmPlatformColumn = getAdditionalColumns(journeysModuleConfiguration).find(
            (column) => column.displayName.trim().toLowerCase() === "utm platform",
        );
        if (eventUtmPlatformColumn !== undefined) {
            return resolveColumnValue(
                getAdditionalColumns,
                {
                    id: eventUtmPlatformColumn.id,
                    label: eventUtmPlatformColumn.displayName,
                    columnId: eventUtmPlatformColumn.id,
                    columnType: "OttopilotEvent",
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                },
                deps,
            );
        }
        if (journeyUtmPlatformColumn !== undefined) {
            return resolveColumnValue(
                getAdditionalColumns,
                {
                    id: journeyUtmPlatformColumn.id,
                    label: journeyUtmPlatformColumn.displayName,
                    columnId: journeyUtmPlatformColumn.id,
                    columnType: "OttopilotEventContainer",
                    isAdditionalColumn: true,
                },
                deps,
            );
        }
        return "-";
    })();
    const tagPillTextResolved =
        isEmotoradLeadEvent === true && tagPillText === "-"
            ? (() => {
                  const digitalRaw = row.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID];
                  if (digitalRaw !== null && digitalRaw !== undefined && String(digitalRaw).trim() !== "") {
                      const digitalId = String(digitalRaw);
                      const fromEnum = Object.values(emotoradEnquiryDigitalLeadStatuses).find((s) => s.id === digitalId);
                      if (fromEnum !== undefined) {
                          return fromEnum.displayName;
                      }
                  }
                  const leadRaw = row.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID];
                  if (leadRaw !== null && leadRaw !== undefined && String(leadRaw).trim() !== "") {
                      const leadId = String(leadRaw);
                      const fromEnum = Object.values(emotoradLeadEventStatuses).find((s) => s.id === leadId);
                      if (fromEnum !== undefined) {
                          return fromEnum.displayName;
                      }
                  }
                  return "-";
              })()
            : tagPillText;
    const leadOwnerUserId = journeysModuleConfiguration.configuration.eventContainersPage.leadOwnerColumnId
        ? (row.eventContainer.additionalColumnValues[journeysModuleConfiguration.configuration.eventContainersPage.leadOwnerColumnId] as Uuid | null)
        : null;
    const leadOwnerDisplayNameFromJourney = getUserDisplayName(leadOwnerUserId, users, userTemplates, platformUserDetailsByUserId);
    const duluxEventOwnerColumnId = isDuluxWorkspaceForPanel === true ? getDuluxEventOwnerColumnIdForEventTemplate(eventTemplate.id) : undefined;
    const duluxEventOwnerResolved =
        duluxEventOwnerColumnId !== undefined
            ? resolveColumnValue(
                getAdditionalColumns,
                {
                    id: duluxEventOwnerColumnId,
                    label: "Event owner",
                    columnId: duluxEventOwnerColumnId,
                    columnType: "OttopilotEvent",
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                },
                deps,
            )
            : "-";
    const leadOwnerDisplayName =
        isDuluxWorkspaceForPanel === true && duluxEventOwnerResolved !== "-" ? duluxEventOwnerResolved : leadOwnerDisplayNameFromJourney;
    // const assigneeLabel =
    //     journeysModuleConfiguration.journeysPage.leadOwnerColumnId !== undefined
    //         ? (journeysModuleConfiguration.additionalColumns.find(
    //             (col) => col.id === journeysModuleConfiguration.journeysPage.leadOwnerColumnId,
    //         )?.displayName ?? "Assignee")
    //         : "Assignee";

    if (isExpanded === true) {
        return (
            <div className="grid grid-flow-row p-ds-space-200">
                <div className="w-full px-ds-space-200 pb-ds-space-150 grid grid-flow-row gap-y-ds-space-100">
                    <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                        <div
                            className="px-ds-space-100 py-ds-space-025 flex items-center"
                            style={{
                                borderRadius: "3px",
                                border: "0.5px solid var(--ds-border-information)",
                                background: "var(--ds-background-accent-blue-subtlest)",
                            }}
                        >
                            <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>{dispositionText}</span>
                        </div>
                    </div>
                    <div className="grid grid-flow-col gap-ds-space-100 items-center justify-between">
                        <AnankeHeading size="medium">{customerName === "" ? "-" : customerName}</AnankeHeading>
                        <div className="border border-ds-border rounded-ds-border-radius">
                            <AnankeIconButton
                                type="button"
                                icon={LinkExternalIcon}
                                label="View lead details"
                                appearance="subtle"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (Date.now() - mountedAtRef.current < 500) {
                                        return;
                                    }
                                    void navigate({
                                        to: buildJourneyProfilePath({
                                            workspaceId: workspace.id,
                                            eventContainerId: row.eventContainer.id,
                                            eventContainerTemplateId: row.eventContainer.templateId,
                                            search: buildEventContainerProfileNavigateSearch(
                                                buildEventContainerProfileFromForEventTemplate(row.templateId),
                                            ),
                                        }),
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <div className="grid grid-flow-col justify-start gap-x-ds-space-100">
                        <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                            <CalendarIcon
                                label=""
                                color="var(--ds-icon-accent-blue)"
                            />
                            <AnankeText size="small">{eventDateTime}</AnankeText>
                        </div>
                        <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                            <TagIcon
                                label=""
                                color="var(--ds-icon-accent-green)"
                            />
                            <AnankeText size="small">{tagPillTextResolved}</AnankeText>
                        </div>
                    </div>
                </div>
                {isScaffoldStyledEventPanel !== true ? (
                    <div className="-mx-ds-space-200 px-ds-space-400 py-ds-space-100 bg-ds-background-accent-blue-subtlest grid grid-cols-[minmax(0,1fr)_max-content] items-center">
                        <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-ds-space-100 items-center min-w-0">
                            <AnankeAvatar
                                size="small"
                                name={leadOwnerDisplayName}
                            />
                            <AnankeText>{leadOwnerDisplayName}</AnankeText>
                        </div>
                        <div
                            className="px-ds-space-100 py-ds-space-025 flex items-center"
                            style={{
                                borderRadius: "4px",
                                background: "var(--ds-icon-inverse)",
                            }}
                        >
                            <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>Assignee</span>
                        </div>
                    </div>
                ) : null}
                <div className="grid grid-flow-row pb-ds-space-200 h-fit">
                    {sections.map(renderSection)}
                    <AnankeTempCollapsibleSectionWithIcon
                        title="Notes"
                        icon={NoteIcon}
                        iconColor="var(--ds-icon-accent-blue)"
                        iconBackgroundColor={token("color.background.accent.teal.subtlest")}
                        initialIsOpen={true}
                        className="border-t border-ds-border py-ds-space-100 px-ds-space-200"
                    >
                        <div className="grid gap-ds-space-150">
                            {noteItems.length === 0 ? (
                                <AnankeText color="color.text.subtlest">No notes yet.</AnankeText>
                            ) : (
                                noteItems.map((item) => (
                                    <NoteFeedItem
                                        key={item.id}
                                        item={item}
                                        canMutate={false}
                                        isEditing={false}
                                        onStartEdit={() => undefined}
                                        onCancelEdit={() => undefined}
                                        onDelete={() => undefined}
                                        truncateEnabled={true}
                                        editComposer={null}
                                    />
                                ))
                            )}
                        </div>
                    </AnankeTempCollapsibleSectionWithIcon>
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-flow-row pb-ds-space-200">
            <div className="w-full px-ds-space-200 pt-ds-space-050 pb-ds-space-150 grid grid-flow-row gap-y-ds-space-100">
                <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                    <div
                        className="px-ds-space-100 py-ds-space-025 flex items-center"
                        style={{
                            borderRadius: "3px",
                            border: "0.5px solid var(--ds-border-information)",
                            background: "var(--ds-background-accent-blue-subtlest)",
                        }}
                    >
                        <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>{dispositionText}</span>
                    </div>
                </div>
                <div className="grid grid-flow-col gap-ds-space-100 items-center justify-between">
                    <AnankeHeading size="medium">{customerName === "" ? "-" : customerName}</AnankeHeading>
                    <div className="border border-ds-border rounded-ds-border-radius">
                        <AnankeIconButton
                            type="button"
                            icon={LinkExternalIcon}
                            label="View lead details"
                            appearance="subtle"
                            onClick={(event) => {
                                event.stopPropagation();
                                if (Date.now() - mountedAtRef.current < 500) {
                                    return;
                                }
                                void navigate({
                                    to: buildJourneyProfilePath({
                                        workspaceId: workspace.id,
                                        eventContainerId: row.eventContainer.id,
                                        eventContainerTemplateId: row.eventContainer.templateId,
                                        search: buildEventContainerProfileNavigateSearch(
                                            buildEventContainerProfileFromForEventTemplate(row.templateId),
                                        ),
                                    }),
                                });
                            }}
                        />
                    </div>
                </div>
                <div className="grid grid-flow-col justify-start gap-x-ds-space-100">
                    <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                        <CalendarIcon
                            label=""
                            color="var(--ds-icon-accent-blue)"
                        />
                        <AnankeText size="small">{eventDateTime}</AnankeText>
                    </div>
                    <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                        <TagIcon
                            label=""
                            color="var(--ds-icon-accent-green)"
                        />
                        <AnankeText size="small">{tagPillTextResolved}</AnankeText>
                    </div>
                </div>
            </div>

            <div className="w-full px-ds-space-200 py-ds-space-100 bg-ds-background-accent-blue-subtlest border-y border-ds-border grid grid-cols-[minmax(0,1fr)_max-content] items-center">
                <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-ds-space-100 items-center min-w-0">
                    <AnankeAvatar
                        size="small"
                        name={leadOwnerDisplayName}
                    />
                    <AnankeText>{leadOwnerDisplayName}</AnankeText>
                </div>
                <div
                    className="px-ds-space-100 py-ds-space-025 flex items-center"
                    style={{
                        borderRadius: "4px",
                        background: "var(--ds-icon-inverse)",
                    }}
                >
                    <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>Assignee</span>
                </div>
            </div>

            <div className="w-full grid grid-flow-row">
                {sections.map(renderSection)}
                <AnankeTempCollapsibleSectionWithIcon
                    title="Notes"
                    icon={NoteIcon}
                    iconColor="var(--ds-icon-accent-blue)"
                    iconBackgroundColor={token("color.background.accent.teal.subtlest")}
                    initialIsOpen={true}
                    className="border-t border-ds-border py-ds-space-100 px-ds-space-200"
                >
                    <div className="grid gap-ds-space-150">
                        {noteItems.length === 0 ? (
                            <AnankeText color="color.text.subtlest">No notes yet.</AnankeText>
                        ) : (
                            noteItems.map((item) => (
                                <NoteFeedItem
                                    key={item.id}
                                    item={item}
                                    canMutate={false}
                                    isEditing={false}
                                    onStartEdit={() => undefined}
                                    onCancelEdit={() => undefined}
                                    onDelete={() => undefined}
                                    truncateEnabled={true}
                                    editComposer={null}
                                />
                            ))
                        )}
                    </div>
                </AnankeTempCollapsibleSectionWithIcon>
            </div>
        </div>
    );
}
